// React & Core
import { GoogleOAuthProvider } from '@react-oauth/google';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import ReactDOM from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import { createBrowserRouter, Navigate, Outlet, RouterProvider } from 'react-router-dom';
import Loader from '@/shared/components/ui/Loader/Loader';
import { DefaultSeo } from '@/shared/components/ui/Seo/Seo';
import { useLoadingStore } from '@/store/loadingStore';

// Styles
import '@/styles/index.css';

// i18n
import '@/shared/utils/i18n.js';

import { ToastContainer } from 'react-toastify';
// Auth
import ForgotPassword from '@/domains/auth/pages/ForgotPassword/ForgotPassword';
import LogIn from '@/domains/auth/pages/LogIn/LogIn';
import NewPassword from '@/domains/auth/pages/NewPassword/NewPassword';
import SignUp from '@/domains/auth/pages/SignUp/SignUp';
import Verification from '@/domains/auth/pages/Verification/Verification';
import {
  FORGOT_PASSWORD_ROUTE,
  NEW_PASSWORD_ROUTE,
  SIGNUP_ROUTE,
  VERIFICATION_ROUTE,
} from '@/domains/auth/utils/authRoutes';
import NotFound from '@/domains/landing-page/pages/NotFound/NotFound';
import AuthorityManagement from '@/domains/system-management/pages/AuthorityManagement/AuthorityManagement';
import CodeManagement from '@/domains/system-management/pages/CodeManagement/CodeManagement';
import GroupManagement from '@/domains/system-management/pages/GroupManagement/GroupManagement';
import LanguageManagement from '@/domains/system-management/pages/LanguageManagement/LanguageManagement';
import ProgramManagement from '@/domains/system-management/pages/ProgramManagement/ProgramManagement';
// System Management
import Unauthorized from '@/domains/system-management/pages/Unauthorized/Unauthorized';
import UserManagement from '@/domains/system-management/pages/UserManagement/UserManagement';
import DevErrorOverlay from '@/shared/components/DevErrorOverlay';
import ErrorBoundary from '@/shared/components/ErrorBoundary';
import ProtectedRoutes from '@/shared/components/layout/ProtectedRoutes/ProtectedRoutes';
import RequireLogin from '@/shared/components/layout/RequireLogin/RequireLogin';
// Shared
import SessionWatcher from '@/shared/components/ui/SessionWatcher/SessionWatcher';
import { PreferenceProvider } from '@/shared/context/preferenceContext.jsx';
import { DEFAULT_LANDING_ROUTE, LOGIN_ROUTE } from '@/shared/utils/loginRedirect';
import 'react-toastify/dist/ReactToastify.css';

// dashboardManagement

import AffiliateDetailManagement from '@/domains/dashboard-management/pages/AffiliateDetailManagement/AffiliateDetailManagement';
import AffiliateManagement from '@/domains/dashboard-management/pages/AffiliateManagement/AffiliateManagement';
import HistoryDetailManagement from '@/domains/dashboard-management/pages/HistoryDetailManagement/HistoryDetailManagement';
import HistoryManagement from '@/domains/dashboard-management/pages/HistoryManagement/HistoryManagement';
import HomeManagement from '@/domains/dashboard-management/pages/HomeManagement/HomeManagement';
import ProductDetailManagement from '@/domains/dashboard-management/pages/ProductDetailManagement/ProductDetailManagement';
import ProductManagement from '@/domains/dashboard-management/pages/ProductManagement/ProductManagement';
// Refund & Pengembalian is hidden for now
// import RefundDetailManagement from '@/domains/dashboard-management/pages/RefundDetailManagement/RefundDetailManagement';
// import RefundManagement from '@/domains/dashboard-management/pages/RefundManagement/RefundManagement';
import Account from '@/domains/mobile-web/pages/Account/Account';
import AccountAddress from '@/domains/mobile-web/pages/AccountAddress/AccountAddress';
import Affiliate from '@/domains/mobile-web/pages/Affiliate/Affiliate';
import Bank from '@/domains/mobile-web/pages/Bank/Bank';
import Book from '@/domains/mobile-web/pages/Book/Book';
import Cart from '@/domains/mobile-web/pages/Cart/Cart';
import ChangePhone from '@/domains/mobile-web/pages/ChangePhone/ChangePhone';
import CheckOut from '@/domains/mobile-web/pages/CheckOut/CheckOut';
import Donation from '@/domains/mobile-web/pages/Donation/Donation';
import EditProfile from '@/domains/mobile-web/pages/EditProfile/EditProfile';
import HistoryAffiliate from '@/domains/mobile-web/pages/HistoryAffiliate/HistoryAffiliate';
// mobileweb
import Home from '@/domains/mobile-web/pages/Home/Home';
import MainScreen from '@/domains/mobile-web/pages/MainScreen/MainScreen';
import Media from '@/domains/mobile-web/pages/Media/Media';
import Payment from '@/domains/mobile-web/pages/Payment/Payment';
import Post from '@/domains/mobile-web/pages/Post/Post';
import PrivacyPolice from '@/domains/mobile-web/pages/PrivacyPolice/PrivacyPolice';
import ProductDetail from '@/domains/mobile-web/pages/ProductDetail/ProductDetail';
import Referral from '@/domains/mobile-web/pages/Referral/Referral';
import Refund from '@/domains/mobile-web/pages/Refund/Refund';
import RefundList from '@/domains/mobile-web/pages/RefundList/RefundList';
import RefundRejected from '@/domains/mobile-web/pages/RefundRejected/RefundRejected';
import RefundStatusDetails from '@/domains/mobile-web/pages/RefundStatusDetails/RefundStatusDetails';
import Reply from '@/domains/mobile-web/pages/Reply/Reply';
import RequestDetails from '@/domains/mobile-web/pages/RequestDetails/RequestDetails';
import SettingAddress from '@/domains/mobile-web/pages/SettingAddress/SettingAddress';
import Tnc from '@/domains/mobile-web/pages/Tnc/Tnc';
import TransactionDetail from '@/domains/mobile-web/pages/TransactionDetail/TransactionDetail';
import TransactionHistory from '@/domains/mobile-web/pages/TransactionHistory/TransactionHistory';
import TransactionTrack from '@/domains/mobile-web/pages/TransactionTrack/TransactionTrack';
import Withdraw from '@/domains/mobile-web/pages/Withdraw/Withdraw';
import ScrollToTop from '@/shared/components/layout/ScrollToTop/ScrollToTop';

const queryClient = new QueryClient();

const router = createBrowserRouter([
  {
    element: (
      <ScrollToTop>
        <DefaultSeo />
        <Outlet />
      </ScrollToTop>
    ),
    children: [
      // Public Routes
      { path: '/', element: <Navigate to={DEFAULT_LANDING_ROUTE} replace /> },
      { path: LOGIN_ROUTE, element: <LogIn /> },
      { path: SIGNUP_ROUTE, element: <SignUp /> },
      { path: VERIFICATION_ROUTE, element: <Verification /> },
      { path: FORGOT_PASSWORD_ROUTE, element: <ForgotPassword /> },
      { path: NEW_PASSWORD_ROUTE, element: <NewPassword /> },
      // Referral link: records the visit for this browser session, then opens the store
      { path: 'ref/:code', element: <Referral /> },

      // Store: home, book list and book detail are open to visitors; every other
      // page goes through RequireLogin (login page, then back to that page).

      {
        path: 'store',
        children: [
          {
            element: <MainScreen />,
            children: [
              { index: true, element: <Navigate to="home" replace /> },
              { path: 'home', element: <Home /> },
              { path: 'book', element: <Book /> },
              {
                element: <RequireLogin />,
                children: [
                  { path: 'account', element: <Account /> },
                  { path: 'donation', element: <Donation /> },
                  { path: 'media', element: <Media /> },
                ],
              },
            ],
          },
          { path: 'tnc', element: <Tnc /> },
          { path: 'privacy-police', element: <PrivacyPolice /> },
          {
            element: <RequireLogin />,
            children: [
              { path: 'payment', element: <Payment /> },
              { path: 'product-detail/:id?', element: <ProductDetail /> },
              { path: 'reply/:id', element: <Reply /> },
              { path: 'post', element: <Post /> },
              { path: 'bank', element: <Bank /> },
              { path: 'withdraw', element: <Withdraw /> },
              { path: 'affiliate', element: <Affiliate /> },
              { path: 'setting/:id?', element: <SettingAddress /> },
              { path: 'address', element: <AccountAddress /> },
              { path: 'history', element: <TransactionHistory /> },
              { path: 'detail', element: <TransactionDetail /> },
              { path: 'track/:id?', element: <TransactionTrack /> },
              { path: 'checkout', element: <CheckOut /> },
              { path: 'cart', element: <Cart /> },
              { path: 'refund', element: <Refund /> },
              { path: 'refund-rejected', element: <RefundRejected /> },
              { path: 'refund-list', element: <RefundList /> },
              { path: 'refund-status', element: <RefundStatusDetails /> },
              { path: 'request/:id', element: <RequestDetails /> },
              { path: 'edit-profile', element: <EditProfile /> },
              { path: 'edit-phone', element: <ChangePhone /> },
              { path: 'history-affiliate', element: <HistoryAffiliate /> },
            ],
          },
        ],
      },

      // Admin (Protected Routes)
      {
        element: <ProtectedRoutes />,
        children: [
          // Management
          {
            path: 'management',
            children: [{ path: 'unauthorized', element: <Unauthorized /> }],
          },

          // System Management
          {
            path: 'system-management',
            children: [
              { path: 'user', element: <UserManagement /> },
              { path: 'program', element: <ProgramManagement /> },
              { path: 'code', element: <CodeManagement /> },
              { path: 'authority', element: <AuthorityManagement /> },
              { path: 'group', element: <GroupManagement /> },
              { path: 'language', element: <LanguageManagement /> },
            ],
          },

          {
            path: 'dashboard',
            children: [
              { path: 'home', element: <HomeManagement /> },
              { path: 'history', element: <HistoryManagement /> },
              { path: 'history-detail', element: <HistoryDetailManagement /> },
              { path: 'product', element: <ProductManagement /> },
              { path: 'product-detail', element: <ProductDetailManagement /> },
              { path: 'affiliate', element: <AffiliateManagement /> },
              { path: 'affiliate-detail', element: <AffiliateDetailManagement /> },
              // Refund & Pengembalian is hidden for now
              // { path: 'refund', element: <RefundManagement /> },
              // { path: 'refund-detail', element: <RefundDetailManagement /> },
            ],
          },
        ],
      },

      { path: '*', element: <NotFound /> },
    ],
  },
]);

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Root element not found');
}

// Fullscreen loader while the translation dictionary is loading (bootstrap /
// language switch) - state is set from ProtectedRoutes, useAuth, SwitchButton.
const TranslationLoader = () => {
  const translationLoading = useLoadingStore((state) => state.translationLoading);
  return <Loader isOpen={translationLoading} />;
};

ReactDOM.createRoot(rootElement).render(
  <ErrorBoundary>
    <HelmetProvider>
      <GoogleOAuthProvider clientId={import.meta.env.VITE_GOOGLE_CLIENT_ID}>
        <QueryClientProvider client={queryClient}>
          <PreferenceProvider>
            <RouterProvider router={router} />
            <TranslationLoader />
            <SessionWatcher />
            <ToastContainer
              position="top-right"
              autoClose={1500}
              hideProgressBar
              closeButton={false}
              className="app-toast-container"
              toastStyle={{
                padding: 0,
                background: 'transparent',
                boxShadow: 'none',
              }}
            />
          </PreferenceProvider>
          <ReactQueryDevtools initialIsOpen={false} />
        </QueryClientProvider>
      </GoogleOAuthProvider>
    </HelmetProvider>
    <DevErrorOverlay />
  </ErrorBoundary>
);
